import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMediaQuery } from 'react-responsive';

import { Drawer } from 'honeydew/Drawer';
import Modal from 'honeydew/Modal';
import useTailwindTheme from 'hooks/useTailwindTheme';
import twclsx from 'utils/twclsx';

const DEFAULT_MODAL_MAX_WIDTH_CLASS_NAME = 'md:max-w-[500px] lg:max-w-[600px]';

/**
 * DrawerModal component that renders as a Drawer on mobile and Modal on desktop
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Content to be displayed
 * @param {boolean} [props.disableClose=false] - Whether to disable closing the modal
 * @param {boolean} [props.hideMobileCloseIcon=false] - When true, omits the default X on mobile so children can provide custom close UI
 * @param {Function} props.onClose - Function called when the modal should be closed
 * @param {Object} [props.drawerProps={}] - Additional props to pass to the Drawer component
 * @param {string} [props.drawerContentClassName] - Optional class names merged with the mobile drawer children wrapper (default padding `p-20`)
 * @param {string} [props.modalContentClassName] - Optional class names merged with the desktop modal content root
 * @param {string} [props.modalMaxWidthClassName] - Desktop-only max-width classes merged with DrawerModal defaults via twclsx
 * @param {Object} [props.modalProps={}] - Additional props to pass to the Modal component
 * @returns {JSX.Element} DrawerModal component
 */
function DrawerModal({
    children,
    disableClose = false,
    drawerContentClassName,
    drawerProps = {},
    hideMobileCloseIcon = false,
    modalContentClassName,
    modalMaxWidthClassName,
    modalProps = {},
    onClose,
    ...rest
}) {
    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const handleClose = () => {
        if (!disableClose) {
            onClose();
        }
    };

    if (mobile) {
        return (
            <Drawer anchor="bottom" open toggleDrawer={handleClose} {...drawerProps}>
                {!hideMobileCloseIcon && (
                    <div className="relative mb-10">
                        <XMarkIcon
                            className="absolute top-10 right-10 size-25 cursor-pointer md:hidden"
                            onClick={handleClose}
                        />
                    </div>
                )}
                <div className={twclsx('p-20', drawerContentClassName)}>{children}</div>
            </Drawer>
        );
    }

    return (
        <Modal
            open
            onClose={handleClose}
            styles={{
                contentRoot: twclsx(DEFAULT_MODAL_MAX_WIDTH_CLASS_NAME, modalMaxWidthClassName, modalContentClassName),
            }}
            {...modalProps}
            {...rest}
        >
            {children}
        </Modal>
    );
}

export default DrawerModal;
